import { Plus } from 'lucide-react';
import { useAlfredInOutreachEnabled } from '../../../../lib/alfred-in-outreach-flag';
import { listedOnTools } from '../../../../lib/outreach-alfred';
import { connectorDisplayName } from '../../../../lib/connectors';
import { matchesConnectorQuery } from '../matching';
import { CONNECTORS_PAGE, MCP_CONNECT_PROMPT, sortConnectors, type AppsController, type McpController } from '../../../../lib/use-connectors';
import { chatHandoffLink } from '../../../../lib/chat-handoff';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { ConnectedStack } from './ConnectedStack';
import { AvailableConnectCard } from './MobileConnectorRow';
import { comingSoonRow, RequestConnectorCard, useAskForConnector } from '../ConnectorRequestCards';
import { McpServersMobile } from './McpServersMobile';
import { EmptyState } from './EmptyState';
import { Button, Card } from '../../ui';
import styles from './mobile.module.css';

/**
 * The Tools half, mobile (Figma 13131-78168 / 78470): section head + "Add", a one-line blurb, the
 * Connected stack, the "Available to Connect" list, and the MCP servers block. Reads the shared
 * `AppsController` passed down from the page — no second fetch, no logic of its own.
 *
 * `query` narrows the lists when the parent has a search open; empty otherwise.
 */
export function ToolsMobile({ controller, mcp, query = '', target }: { controller: AppsController; mcp: McpController; query?: string; target?: string }) {
  const alfredOnOutreach = useAlfredInOutreachEnabled();
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const basePath = useBasePath();
  const { state } = controller;
  const { askComingSoon } = useAskForConnector();
  const connectHandoff = chatHandoffLink({ kind: 'new', message: MCP_CONNECT_PROMPT }, basePath);

  const header = (
    <div className={styles.sectionHead}>
      <h2 className={styles.sectionTitleLg}>{t('connectors.tab.tools')}</h2>
      <Button
        variant="text"
        size="small"
        href={connectHandoff.route}
        linkAs={connectHandoff.path}
        startIcon={<Plus size={16} aria-hidden />}
        onClick={() => logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'add_tool' })}
      >
        {t('common.add')}
      </Button>
    </div>
  );

  if (state.kind === 'loading') {
    return (
      <div className={styles.toolsBlock}>
        {header}
        <ToolsSkeleton />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className={styles.toolsBlock}>
        {header}
        <div role="alert" className={styles.errorLine}>{t('connections.error')}</div>
      </div>
    );
  }

  const searching = query.trim().length > 0;
  const q = query.trim().toLowerCase();
  const visible = searching
    ? state.connectors.filter((c) => matchesConnectorQuery(connectorDisplayName(c), c.toolkit, q))
    : state.connectors;
  const connected = sortConnectors(visible.filter((c) => c.connected && listedOnTools(c, alfredOnOutreach)));
  const available = sortConnectors(visible.filter((c) => !c.connected && listedOnTools(c, alfredOnOutreach)));
  const comingSoon = state.comingSoon.map(comingSoonRow).filter((c) => !searching || matchesConnectorQuery(connectorDisplayName(c), c.toolkit, q));
  const availableCount = available.length + comingSoon.length;

  return (
    <div className={styles.toolsBlock}>
      {header}
      <p className={styles.sectionSub}>{t('connectors.section.subtitle')}</p>
      <hr className={styles.sectionDivider} />

      {/* Empty state (Figma 13131-78378): nothing connected yet → the "No Connectors yet" card. */}
      {connected.length === 0 && !searching && <EmptyState />}

      {connected.length > 0 && <ConnectedStack connected={connected} controller={controller} target={target} />}

      <section>
        <h3 className={styles.sectionTitle}>
          {t('connectors.apps.available')}
          {availableCount > 0 && <span className={styles.sectionCount}>{` · ${availableCount}`}</span>}
        </h3>
        <div className={styles.list}>
          {available.map((connector) => (
            <AvailableConnectCard
              key={connector.toolkit}
              connector={connector}
              busy={controller.busy}
              onConnect={controller.connect}
            />
          ))}
          {comingSoon.map((connector) => (
            <AvailableConnectCard key={`coming-soon-${connector.toolkit}`} connector={connector} busy={NOTHING_BUSY} onConnect={askComingSoon} />
          ))}
          <RequestConnectorCard key={query} compact initialText={availableCount === 0 ? query.trim() : ''} />
        </div>
      </section>

      {controller.actionError && <div role="alert" className={styles.errorLine}>{controller.actionError}</div>}

      <McpServersMobile appsState={state} connectCta={connectHandoff} controller={mcp} />
    </div>
  );
}

const NOTHING_BUSY: ReadonlySet<string> = new Set();

function ToolsSkeleton() {
  return (
    <div className={styles.list}>
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i} className={styles.connectorCard}>
          <div className={styles.rowMain}>
            <div className="orion-shimmer" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
            <div className={styles.rowText} style={{ gap: 8 }}>
              <div className="orion-shimmer" style={{ height: 13, width: '45%' }} />
              <div className="orion-shimmer" style={{ height: 11, width: '80%' }} />
            </div>
            <div className="orion-shimmer" style={{ width: 74, height: 30, borderRadius: 10, flexShrink: 0 }} />
          </div>
        </Card>
      ))}
    </div>
  );
}
